import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, BadgeCheck, ShieldCheck, Lock } from "lucide-react";
import { apiClient, API } from "@/lib/api";
import { FALLBACK_REVIEWS } from "./data";
import { SectionHeading } from "./Services";

const GUARANTEES = [
  { icon: BadgeCheck, text: "Free originality report with every order" },
  { icon: ShieldCheck, text: "100% human writers — verified AI-free" },
  { icon: Lock, text: "Your identity & documents stay private" },
];

export const Reviews = () => {
  const [reviews, setReviews] = useState(FALLBACK_REVIEWS);

  useEffect(() => {
    apiClient.get("/reviews").then((r) => {
      if (r.data?.length) setReviews(r.data);
    }).catch(() => {});
  }, []);

  return (
    <section id="reviews" className="border-y border-[#D4AF37]/10 bg-[#F3EEE2]" data-testid="reviews-section">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36">
        <SectionHeading id="reviews" overline="Verified Student Reviews" title="Trusted from London" accent="to Doha." />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {reviews.map((t, i) => (
            <motion.figure
              key={t.id || t.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 2) * 0.12, ease: [0.33, 1, 0.68, 1] }}
              className="luxury-card rounded-xl p-7 sm:p-10"
              data-testid={`review-card-${i}`}
            >
              <div className="mb-5 flex gap-1" data-testid={`review-rating-${i}`}>
                {Array.from({ length: t.rating }).map((_, s) => (
                  <Star key={s} size={15} className="fill-[#D4AF37] text-[#A07E1F]" />
                ))}
              </div>
              <blockquote className="font-serif-display text-xl italic leading-relaxed text-[#12294D] sm:text-2xl">
                "{t.quote}"
              </blockquote>
              <figcaption className="mt-7 flex items-center gap-4">
                <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 font-serif-display text-lg font-semibold text-[#A07E1F]">
                  {t.image_path ? (
                    <img src={`${API}/reviews/${t.id}/image`} alt={t.name} className="h-full w-full object-cover" data-testid={`review-image-${i}`} />
                  ) : (
                    t.name.charAt(0)
                  )}
                </span>
                <span>
                  <span className="block text-sm font-semibold text-[#12294D]">{t.name}</span>
                  <span className="block font-mono-accent text-[10px] uppercase tracking-[0.18em] text-[#5E6E85]">{t.meta}</span>
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-12 flex flex-col gap-4 sm:flex-row sm:gap-10"
          data-testid="reviews-guarantees"
        >
          {GUARANTEES.map((g) => (
            <p key={g.text} className="flex items-center gap-3 text-sm text-[#46586F]">
              <g.icon size={16} className="shrink-0 text-[#A07E1F]" /> {g.text}
            </p>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
